import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Divider,
  TextField,
  Button
} from '@mui/material';
import { WhatsApp as WhatsAppIcon } from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import SuperAdminLayout from '../components/SuperAdminLayout';
import { getPlatformSettings, updatePlatformSettings } from '../services/platformSettingsService';
import { changeSuperAdminPassword } from '../services/superAdminAuthService';

/**
 * System-wide settings (Super Admin).
 */
const SettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [whatsappOtpEnabled, setWhatsappOtpEnabled] = useState(true);
  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const handlePasswordChange = async (event) => {
    event.preventDefault();
    if (passwordSaving) return;
    setPasswordError('');
    setPasswordSuccess(false);
    if (passwords.newPassword !== passwords.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (!passwords.newPassword.trim() || passwords.newPassword.length < 6
        || new TextEncoder().encode(passwords.newPassword).length > 72) {
      setPasswordError('Use at least 6 characters and at most 72 bytes for the new password.');
      return;
    }
    setPasswordSaving(true);
    try {
      await changeSuperAdminPassword(passwords.currentPassword, passwords.newPassword);
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordSuccess(true);
    } catch (e) {
      setPasswordError(e.response?.data?.message || e.response?.data?.error || 'Could not change password. Please try again.');
    } finally {
      setPasswordSaving(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getPlatformSettings();
        if (!cancelled) setWhatsappOtpEnabled(!!data.enableWhatsAppRegistrationOtp);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleToggle = async (_event, checked) => {
    const previous = whatsappOtpEnabled;
    setWhatsappOtpEnabled(checked);
    setSaving(true);
    try {
      await updatePlatformSettings({ enableWhatsAppRegistrationOtp: checked });
      toast.success(checked ? 'WhatsApp OTP enabled for registration' : 'WhatsApp OTP disabled — tenants can register without OTP');
    } catch (e) {
      setWhatsappOtpEnabled(previous);
      toast.error(e.response?.data?.message || e.message || 'Could not save setting');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SuperAdminLayout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, color: '#2c3e50' }}>
            System Settings
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Configure system-wide settings for all tenants and registration flows.
          </Typography>
        </Box>

        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              <Box display="flex" alignItems="flex-start" gap={2}>
                <WhatsAppIcon color="primary" sx={{ mt: 0.5 }} />
                <Box flex={1}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    WhatsApp OTP on company registration
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
                    When enabled, new companies must verify their mobile number with a WhatsApp OTP before continuing.
                    Turn this off if your WhatsApp template is not approved yet — registrants can enter a mobile number and go to the next step without verification (same as the older flow).
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={whatsappOtpEnabled}
                        onChange={handleToggle}
                        disabled={saving}
                        color="primary"
                      />
                    }
                    label={whatsappOtpEnabled ? 'Enabled' : 'Disabled'}
                  />
                  {saving && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      Saving…
                    </Typography>
                  )}
                </Box>
              </Box>
            </>
          )}
        </Paper>
        <Paper elevation={2} sx={{ p: 3, mt: 3, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Change super admin password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Update the password for your signed-in super admin account.
          </Typography>
          <Box component="form" onSubmit={handlePasswordChange} sx={{ maxWidth: 480 }}>
            {passwordError && <Alert severity="error" sx={{ mb: 2 }}>{passwordError}</Alert>}
            {passwordSuccess && <Alert severity="success" sx={{ mb: 2 }}>Your super admin password has been changed.</Alert>}
            {[
              ['currentPassword', 'Current password', 'current-password'],
              ['newPassword', 'New password', 'new-password'],
              ['confirmPassword', 'Confirm new password', 'new-password']
            ].map(([field, label, autoComplete]) => (
              <TextField key={field} id={`superadmin-${field}`} name={field} label={label}
                type="password" autoComplete={autoComplete} required fullWidth margin="normal"
                disabled={passwordSaving} value={passwords[field]}
                helperText={field === 'newPassword' ? 'At least 6 characters.' : undefined}
                onChange={(event) => {
                  setPasswords({ ...passwords, [field]: event.target.value });
                  setPasswordError('');
                  setPasswordSuccess(false);
                }} />
            ))}
            <Button type="submit" variant="contained" disabled={passwordSaving} sx={{ mt: 2 }}>
              {passwordSaving ? 'Changing password…' : 'Change password'}
            </Button>
          </Box>
        </Paper>
      </Container>
    </SuperAdminLayout>
  );
};

export default SettingsPage;
